/**
 * Umrechnung zwischen der Auswahl im Formular und einer RFC-5545-Regel.
 *
 * Bewusst getrennt von der Komponente: reine Funktionen, ohne React, testbar.
 */
import { RRule, rrulestr } from 'rrule';

import { formatDe } from '@/lib/datetime';
import { fromFloatingUtc, toFloatingUtc } from '@/lib/recurrence/expand';
import type { LocalDate, LocalDateTime } from '@/domain/types';

export type Frequency = 'keine' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: 'keine', label: 'Nie' },
  { value: 'DAILY', label: 'Täglich' },
  { value: 'WEEKLY', label: 'Wöchentlich' },
  { value: 'MONTHLY', label: 'Monatlich' },
  { value: 'YEARLY', label: 'Jährlich' },
];

/** Wie eine Serie endet (Roadmap F-03): nie, an einem Datum, nach n Terminen. */
export type RecurrenceEnd = 'nie' | 'datum' | 'anzahl';

export interface RecurrenceValue {
  frequency: Frequency;
  interval: number;
  /** Wochentage als RFC-Kuerzel, nur bei WEEKLY. */
  byDay: string[];
  ende: RecurrenceEnd;
  /** Letzter Tag, nur bei `ende: 'datum'`. */
  until: LocalDate | '';
  /** Anzahl der Termine, nur bei `ende: 'anzahl'`. */
  count: number;
  /**
   * Der gespeicherte UNTIL-Stempel, unveraendert. Eine abgeschnittene Serie
   * endet z. B. um 17:59 - das Formular zeigt nur den Tag. Bleibt der Tag,
   * bleibt der Stempel; sonst kaeme der 18:00-Termin doppelt zurueck.
   */
  untilStamp?: string;
}

/** Vorgabe fuer "Nach n Terminen" - wird erst beim Umschalten sichtbar. */
export const DEFAULT_COUNT = 10;

export function emptyRecurrence(): RecurrenceValue {
  return { frequency: 'keine', interval: 1, byDay: [], ende: 'nie', until: '', count: DEFAULT_COUNT };
}

/** Baut aus der Auswahl die RFC-5545-Regel. */
export function toRrule(value: RecurrenceValue, start: string): string | undefined {
  if (value.frequency === 'keine') return undefined;

  const parts = [`FREQ=${value.frequency}`];
  if (value.interval > 1) parts.push(`INTERVAL=${value.interval}`);

  if (value.frequency === 'WEEKLY') {
    // Ohne Auswahl gilt der Wochentag des Starttermins.
    const days = value.byDay.length > 0 ? sortWeekdays(value.byDay) : [weekdayOf(start)];
    parts.push(`BYDAY=${days.join(',')}`);
  }

  // UNTIL oder COUNT, nie beides - RFC 5545 verbietet die Kombination.
  if (value.ende === 'datum' && value.until) {
    const tag = value.until.replace(/-/g, '');
    // Bis zum Ende des gewaehlten Tages, damit der Tag selbst noch zaehlt -
    // ausser der Tag ist unveraendert, dann gilt der gespeicherte Stempel.
    parts.push(`UNTIL=${value.untilStamp?.startsWith(tag) ? value.untilStamp : `${tag}T235900Z`}`);
  } else if (value.ende === 'anzahl') {
    parts.push(`COUNT=${clampCount(value.count)}`);
  }

  return parts.join(';');
}

/** Liest eine gespeicherte Regel zurueck in die Auswahl. */
export function fromRrule(rule: string | undefined): RecurrenceValue {
  if (!rule) return emptyRecurrence();

  const map: Record<string, string> = {};
  for (const part of rule.replace(/^RRULE:/i, '').split(';')) {
    const [k, v] = part.split('=');
    if (k && v) map[k.toUpperCase()] = v;
  }

  const freq = map.FREQ as Frequency | undefined;
  const until = map.UNTIL
    ? (`${map.UNTIL.slice(0, 4)}-${map.UNTIL.slice(4, 6)}-${map.UNTIL.slice(6, 8)}` as LocalDate)
    : '';

  const count = map.COUNT ? clampCount(+map.COUNT) : undefined;

  return {
    frequency: freq && FREQUENCIES.some((f) => f.value === freq) ? freq : 'keine',
    interval: Math.max(1, +(map.INTERVAL ?? '1')),
    byDay: map.BYDAY ? sortWeekdays(map.BYDAY.split(',')) : [],
    ende: count !== undefined ? 'anzahl' : until ? 'datum' : 'nie',
    until,
    count: count ?? DEFAULT_COUNT,
    ...(map.UNTIL ? { untilStamp: map.UNTIL } : {}),
  };
}

/** Mindestens ein Termin, hoechstens so viele, wie die Expansion je zeigt. */
export function clampCount(n: number): number {
  return Number.isFinite(n) ? Math.min(999, Math.max(1, Math.round(n))) : 1;
}

export interface OccurrencePreview {
  /** Die ersten Termine der Serie. */
  first: LocalDateTime[];
  /** Der letzte Termin einer endlichen Serie, sonst `undefined`. */
  last?: LocalDateTime;
  /** Gesamtzahl einer endlichen Serie (bis 999), sonst `undefined`. */
  total?: number;
}

/**
 * Die ersten Termine einer Regel - fuer die Vorschau im Dialog.
 *
 * Macht sichtbar, was die Regel wirklich tut: passt der Starttag nicht zu den
 * gewaehlten Wochentagen, ist der erste Termin ein anderer Tag; bei "alle 2
 * Wochen" sieht man, welche Woche gemeint ist. Rechnet wie die Expansion in
 * floating space (siehe `lib/recurrence/expand.ts`).
 */
export function previewOccurrences(rule: string | undefined, start: string, n = 3): OccurrencePreview {
  if (!rule) return { first: [] };
  try {
    const parsed = rrulestr(`RRULE:${rule}`, { dtstart: toFloatingUtc(start) });
    if (!(parsed instanceof RRule)) return { first: [] };
    const first = parsed.all((_, i) => i < n).map(fromFloatingUtc);
    if (!parsed.options.count && !parsed.options.until) return { first };
    const alle = parsed.all((_, i) => i < 999);
    const last = alle[alle.length - 1];
    return { first, last: last ? fromFloatingUtc(last) : undefined, total: alle.length };
  } catch {
    return { first: [] };
  }
}

/** Immer in Wochenreihenfolge ab Montag - sonst liest sich die Regel wirr. */
function sortWeekdays(days: string[]): string[] {
  return [...days].sort(
    (a, b) => WEEKDAY_CODES.indexOf(a as never) - WEEKDAY_CODES.indexOf(b as never),
  );
}

/** Der Wochentag des Starttermins als RFC-Kuerzel. */
export function weekdayOf(start: string): string {
  // 'i' liefert den ISO-Wochentag 1..7 (Montag = 1).
  return WEEKDAY_CODES[+formatDe(start, 'i') - 1] ?? 'MO';
}

export function unitFor(frequency: Frequency, interval: number): string {
  const one = interval === 1;
  switch (frequency) {
    case 'DAILY': return one ? 'Tag' : 'Tage';
    case 'WEEKLY': return one ? 'Woche' : 'Wochen';
    case 'MONTHLY': return one ? 'Monat' : 'Monate';
    case 'YEARLY': return one ? 'Jahr' : 'Jahre';
    default: return '';
  }
}
